import { useCallback, useEffect, useRef, useState } from 'react'

import type { CalendarProps, DateRange, FlowDirection, TimeUnitMode } from './calendar.types'

import Box from '../box'
import { omitThemingProps, useStyleConfig, vui } from '../core'
import { cs } from '../utils'
import { CalendarHeader, DayPicker, MonthPicker, YearPicker } from './components'
import { useCalendar, useDefaultViewMonth } from './hooks'
import {
  isDay,
  isExact,
  isFrom,
  isMonth,
  isTo,
  isYear,
  processSelectMonth,
  processSelectYear,
  processViewDate,
} from './utils'

/** Displays calendar. */
export const Calendar = vui<'div', CalendarProps>((props, ref) => {
  const {
    boundaries,
    className,
    fixedNumberOfWeeks = false,
    disabled,
    isStartDate,
    mode,
    onSelectDates,
    selectedDates,
    ...rest
  } = omitThemingProps(props)
  const styles = useStyleConfig('Calendar', props)

  const currentDateRef = useRef(isFrom(mode) || isExact(mode) ? selectedDates?.startDate : selectedDates?.endDate)

  const defaultViewMonth = useDefaultViewMonth({
    boundaries,
    selectedDate: currentDateRef.current,
    isStartDate,
  })

  const [timeUnitMode, setTimeUnitMode] = useState<TimeUnitMode>('day')
  const [viewDate, setViewDate] = useState<Date>(defaultViewMonth)

  const calendar = useCalendar({
    boundaries,
    mode,
    selectedDates,
    timeUnitMode,
    viewDate,
    fixedNumberOfWeeks,
  })

  /**
   * Effect to reset viewDate when selectedDates changes (onReset)
   * Compare timestamps to avoid resetting the view when the object reference changes but the date value hasn't.
   */
  useEffect(() => {
    const newDate = isFrom(mode) || isExact(mode) ? selectedDates?.startDate : selectedDates?.endDate
    if (newDate?.getTime() !== currentDateRef.current?.getTime()) {
      currentDateRef.current = newDate
      if (newDate) setViewDate(newDate)
    }
  }, [selectedDates, mode])

  /**
   * Send update date to parent component
   */
  const updateDateState = useCallback(
    (newDate: Date) => {
      const dateRange: DateRange = { ...selectedDates }
      if (isTo(mode)) dateRange.endDate = newDate
      else dateRange.startDate = newDate
      if (newDate && !disabled) onSelectDates?.(dateRange)
    },
    [selectedDates, mode, disabled, onSelectDates],
  )

  /**
   * Handles clicking next/prev in header
   */
  const onChangeViewItem = useCallback(
    (direction: FlowDirection) => {
      const newViewMonth = processViewDate(viewDate, direction, timeUnitMode, calendar.items)
      if (newViewMonth) setViewDate(newViewMonth)
    },
    [viewDate, timeUnitMode, calendar.items],
  )

  /**
   * Handles clicking on a day, month or year cell. The active timeUnitMode decides
   * how the clicked date is processed and which unit to drill down into next.
   */
  const onSelect = useCallback(
    (clickedDate: Date) => () => {
      let processedDate = clickedDate

      if (isMonth(timeUnitMode)) {
        processedDate = processSelectMonth(clickedDate, mode, boundaries)
        setTimeUnitMode('day')
      } else if (isYear(timeUnitMode)) {
        processedDate = processSelectYear(clickedDate, mode, boundaries)
        setTimeUnitMode('month')
      }

      updateDateState(processedDate)
      setViewDate(processedDate)
    },
    [timeUnitMode, mode, boundaries, updateDateState],
  )

  return (
    <Box
      className={cs('vui-calendar', className)}
      column
      cursor="default"
      minW={280}
      position="relative"
      ref={ref}
      w="fit-content"
      {...styles}
      {...rest}
    >
      <CalendarHeader
        nextDisabled={calendar.nextDisabled}
        onChangeViewItem={onChangeViewItem}
        onSetTimeUnitMode={setTimeUnitMode}
        prevDisabled={calendar.prevDisabled}
        timeUnitMode={timeUnitMode}
        viewMonth={viewDate}
      />
      {isDay(timeUnitMode) && <DayPicker calendar={calendar} onSelectDay={onSelect} />}
      {isMonth(timeUnitMode) && <MonthPicker calendar={calendar} onSelectMonth={onSelect} />}
      {isYear(timeUnitMode) && <YearPicker calendar={calendar} onSelectYear={onSelect} />}
    </Box>
  )
})

Calendar.displayName = 'Calendar'

export default Calendar
